/**
 * CounterOfferModal — submit a counter-offer with adjusted terms.
 *
 * Replaces the inline modal previously embedded in OfferManagement.js
 * (lines ~1577–1661). Uses ui/Modal for the shell.
 *
 * Price sanity gate (QA sheet T-41): a counter more than
 * COUNTER_PRICE_TOLERANCE off the current asking price is almost always a
 * typo — a stray digit turns $655,000 into $6,550,000. Rather than block it
 * (a seller may genuinely counter far above asking in a hot market), we
 * surface the delta and require an explicit confirmation before the submit
 * button unlocks. Inert when we have no asking price to compare against.
 *
 * The gate arms only once the user has edited the price. The form opens
 * pre-filled with the *other party's* price, and an ordinary lowball is more
 * than 20% off asking — warning someone about a typo in a number they never
 * typed, and blocking submit until they vouch for it, is not the job.
 */
import React, { useEffect, useId, useMemo, useState } from 'react';
import Modal from '../../ui/Modal';
import {
  CONTINGENCIES_UNCHANGED_NOTE,
  CONTINGENCY_UNAVAILABLE_MESSAGE,
  CURE_ELECTION_FIELD,
  FINANCING_KEY,
  FINANCING_TERMS_FIELD,
  TITLE_PERIODS_FIELD,
  cureElectionAsked,
  cureElectionCategory,
  cureElectionFromOffer,
  deadlineKey,
  financingLoanAmount,
  financingTermsAsked,
  financingTermsFieldErrors,
  financingTermsFromOffer,
  getContingencyOptionsForOffer,
  isWisconsinHomeOffer,
  leadPaintElectionFromOffer,
  leadPaintElectionMode,
  normalizeContingencies,
  normalizeContingencyDeadlines,
  offerElectionError,
  offerTermsCategory,
  parseContingencyElectionError,
  serializeContingencies,
  titlePeriodsFromOffer,
  unavailableSelectedKeys,
  withoutFinancingTerms,
} from '../contingencyCatalog';
import { ContingencyErrorBanner, ContingencyFieldError } from '../ContingencyElectionErrors';
import ContingencyDeadlineField from '../ContingencyDeadlineField';
import LandContingencyNote, { ContingencyDeadlineNote } from '../LandContingencyNote';
import LeadPaintElectionField, { LeadPaintElectionCarried } from '../LeadPaintElectionField';
import CureElectionField from '../CureElectionField';
import FinancingTermsField from '../FinancingTermsField';
import TitlePeriodsField from '../TitlePeriodsField';
import { priceDeviation, isCounterPriceOutOfRange } from '../../../utils/counterPrice';
import {
  counterEarnestError,
  counterEarnestPayload,
  initialCounterEarnest,
} from '../../../utils/counterEarnest';
import {
  deadlineTimeLabel,
  getDefaultExpiresOn,
  getExpirationBounds,
  getExpirationError,
} from '../offerExpiration';
import OfferDeadlineDisclaimer from '../OfferDeadlineDisclaimer';
import './CounterOfferModal.css';

/**
 * Seed the counter form from the offer being countered.
 *
 * Contingencies are copied verbatim — never defaulted on — so a counter
 * preserves exactly what the parties already agreed to. Options the offer did
 * not carry start unchecked. `contingenciesTouched` stays false until the user
 * changes a contingency: an untouched counter omits `contingencies`, and the
 * server carries the stored terms forward (so a price-only counter on an older
 * offer, e.g. one with a blank deadline, still goes through).
 *
 * @param {Object|null|undefined} offer - Offer being countered.
 * @param {Array<Object>} options - Catalog entries rendered by the modal.
 * @returns {Object} Counter form state.
 */
const initialFormFromOffer = (offer, options) => {
  const recorded = normalizeContingencies(offer?.contingencies);
  // Negotiated deadlines (`<key>_days`), the lead-paint and cure elections,
  // the loan terms and the title periods carry over too.
  const contingencies = {
    ...normalizeContingencyDeadlines(offer?.contingencies),
    ...leadPaintElectionFromOffer(offer?.contingencies),
    ...cureElectionFromOffer(offer?.contingencies),
    ...financingTermsFromOffer(offer?.contingencies),
    ...titlePeriodsFromOffer(offer?.contingencies),
  };
  options.forEach(({ key }) => {
    contingencies[key] = !!recorded[key];
  });
  return {
    offer_price: offer?.offer_price ? String(offer.offer_price) : '',
    down_payment_percent:
      offer?.down_payment_percent !== undefined && offer?.down_payment_percent !== null
        ? Number(offer.down_payment_percent)
        : 20,
    // The deposit on the table; blank keeps it (QA D1/N15).
    earnest_money: initialCounterEarnest(offer?.earnest_money),
    closing_date: offer?.closing_date || '',
    // A counter is a new offer with its own acceptance deadline; it does not
    // inherit the one it answers.
    expires_on: getDefaultExpiresOn(offer?.closing_date, offer?.property_timezone),
    contingencies,
    contingenciesTouched: false,
    changes_description: '',
  };
};

export default function CounterOfferModal({
  open,
  onClose,
  offer,
  currentRole,
  property,
  submitting = false,
  onSubmit,
  error,
  // The API error's `detail`; a 422 offer_contingencies_invalid is shown next
  // to the contingencies it names.
  errorDetail = null,
}) {
  const options = useMemo(
    () => getContingencyOptionsForOffer(offer, property),
    [offer, property]
  );
  // Pre-1978 or unknown-year WI home: the buyer's inspect/waive election.
  const leadPaintMode = useMemo(() => leadPaintElectionMode(property, offer || {}), [offer, property]);
  // The election is the buyer's right: a seller's counter shows it read-only
  // and carries it forward; only the buyer is asked.
  const leadPaintAskedMode = currentRole === 'seller' ? null : leadPaintMode;
  // WI offer: the 7(d) cure election, asked while a curable contingency is selected.
  const cureCategory = useMemo(() => cureElectionCategory(property, offer || {}), [offer, property]);
  // WI offer: the title periods, and the loan terms while financing is selected (QA N13).
  const termsCategory = useMemo(() => offerTermsCategory(property, offer || {}), [offer, property]);
  const [form, setForm] = useState(() => initialFormFromOffer(offer, options));
  // Cleared whenever the price changes, so confirming once then typing a new
  // out-of-range price re-arms the gate.
  const [priceConfirmed, setPriceConfirmed] = useState(false);
  // The gate is about typos, so it stays disarmed until the user edits the
  // price. Without this a lowball offer opens the modal already blocked.
  const [priceTouched, setPriceTouched] = useState(false);

  // ids for label / aria-describedby pairing
  const introId = useId();
  const priceId = useId();
  const priceHintId = useId();
  const dpId = useId();
  const dpHintId = useId();
  const earnestId = useId();
  const earnestHintId = useId();
  const closingId = useId();
  const closingHintId = useId();
  const expiresId = useId();
  const expiresHintId = useId();
  const descId = useId();
  const errorId = useId();
  const priceWarnId = useId();

  // Reset on open transition or when target offer changes
  useEffect(() => {
    if (open) {
      setForm(initialFormFromOffer(offer, options));
      setPriceConfirmed(false);
      setPriceTouched(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, offer?.id]);

  const priceNumber = Number(form.offer_price);
  const dpNumber = Number(form.down_payment_percent);
  const earnestError = counterEarnestError(form.earnest_money, priceNumber);
  const currentEarnest = initialCounterEarnest(offer?.earnest_money);

  const listPrice = offer?.list_price;
  const deviation = priceDeviation(priceNumber, listPrice);
  const priceOutOfRange =
    priceTouched && isCounterPriceOutOfRange(priceNumber, listPrice);
  const needsPriceConfirm = priceOutOfRange && !priceConfirmed;
  // Deadlines must be 1-365 days; on every WI offer each selected contingency
  // needs one (legal L4 §9.7; user decision for home and condo); a term the
  // listing's offer can't print must go; the lead-paint and cure elections
  // are required where they are asked, and on a WI offer the title periods
  // and the financing contingency's loan terms (QA N13).
  const deadlineError = offerElectionError(form.contingencies, options, leadPaintAskedMode, cureCategory, {
    category: termsCategory,
    downPaymentPercent: form.down_payment_percent,
  });
  const cureAsked = cureElectionAsked(cureCategory, form.contingencies);
  const financingAsked = financingTermsAsked(termsCategory, form.contingencies);
  // A 100% down payment leaves the financing contingency no loan, even when
  // the contingencies are carried forward untouched (the server refuses it).
  const fullDownPaymentError = financingAsked
    ? financingTermsFieldErrors(form.contingencies, termsCategory, form.down_payment_percent)[FINANCING_KEY] || null
    : null;
  // Only elections the user changed are checked and sent; untouched ones
  // carry forward. A term this listing's offer can't print blocks either way.
  const deadlinesInvalid = form.contingenciesTouched
    ? Boolean(deadlineError)
    : unavailableSelectedKeys(form.contingencies, options).length > 0 || Boolean(fullDownPaymentError);
  const hasLandContingencies = options.some((o) => o.group === 'land');
  const showDeadlineNote = !hasLandContingencies && isWisconsinHomeOffer(property, offer || {});

  // The server's refusal describes the elections that were submitted; it
  // stops applying once they change.
  const contingencyError = useMemo(() => parseContingencyElectionError(errorDetail), [errorDetail]);
  const submittedContingencies = React.useRef(null);
  const contingencyErrorActive =
    Boolean(contingencyError) && form.contingencies === submittedContingencies.current;
  const serverErrors = contingencyErrorActive ? contingencyError.fieldErrors : {};

  const timeZone = offer?.property_timezone;
  const expiresBounds = getExpirationBounds(form.closing_date, timeZone);
  const expirationError = getExpirationError(form.expires_on, form.closing_date, timeZone);
  const recipient = currentRole === 'seller' ? 'buyer' : 'seller';

  const canSubmit =
    priceNumber > 0 &&
    dpNumber >= 0 &&
    dpNumber <= 100 &&
    !earnestError &&
    !!form.closing_date &&
    !expirationError &&
    !needsPriceConfirm &&
    !deadlinesInvalid &&
    !submitting;

  const submitTitle = submitting
    ? 'Submitting…'
    : needsPriceConfirm
    ? 'Confirm the price is intentional before submitting.'
    : earnestError
    ? earnestError
    : deadlinesInvalid
    ? (form.contingenciesTouched ? deadlineError : fullDownPaymentError || deadlineError)
    : expirationError && form.closing_date
    ? expirationError
    : !canSubmit
    ? 'Enter a valid price, down payment %, and closing date.'
    : undefined;

  const handleSubmit = () => {
    if (!canSubmit) return;
    submittedContingencies.current = form.contingencies;
    onSubmit?.({
      offer_id: offer?.id,
      offer_price: priceNumber,
      down_payment_percent: dpNumber,
      // Blank is omitted: the server keeps the deposit on the table.
      ...counterEarnestPayload(form.earnest_money),
      closing_date: form.closing_date,
      expires_on: form.expires_on,
      // A changed set goes out whole, every offered contingency as an explicit
      // election. Unchanged, it is omitted: the server keeps the stored terms.
      ...(form.contingenciesTouched
        ? {
            contingencies: serializeContingencies(form.contingencies, {
              explicitKeys: options.map((o) => o.key),
              leadPaintMode,
              cureCategory,
              termsCategory,
            }),
          }
        : {}),
      changes_description: form.changes_description || '',
    });
  };

  const handleContingencyChange = (key, checked) => {
    setForm((prev) => {
      let contingencies = { ...prev.contingencies, [key]: checked };
      if (!checked) delete contingencies[deadlineKey(key)];
      if (!checked && key === FINANCING_KEY) contingencies = withoutFinancingTerms(contingencies);
      return { ...prev, contingencies, contingenciesTouched: true };
    });
  };

  const handleTermsChange = (contingencies) =>
    setForm((prev) => ({ ...prev, contingencies, contingenciesTouched: true }));

  const handleDeadlineChange = (key, text) => {
    setForm((prev) => ({
      ...prev,
      contingencies: { ...prev.contingencies, [deadlineKey(key)]: text },
      contingenciesTouched: true,
    }));
  };

  const footer = (
    <>
      {error && !contingencyError && (
        <p
          id={errorId}
          role="alert"
          className="counter-modal__error"
        >
          {error}
        </p>
      )}
      <div className="counter-modal__actions">
        <button
          type="button"
          className="btn-secondary"
          onClick={onClose}
          disabled={submitting}
        >
          Cancel
        </button>
        <button
          type="button"
          className="btn-primary"
          onClick={handleSubmit}
          disabled={!canSubmit}
          title={submitTitle}
          aria-describedby={error ? errorId : undefined}
        >
          {submitting ? 'Submitting…' : 'Submit Counter'}
        </button>
      </div>
    </>
  );

  const propertyLabel =
    offer?.property_address || (offer?.property_id ? `property #${offer.property_id}` : 'this offer');

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Submit a counter-offer"
      size="md"
      describedBy={introId}
      footer={footer}
    >
      <p id={introId} className="counter-modal__intro">
        Adjust the terms below for <strong>{propertyLabel}</strong>. The other party will see your changes.
      </p>

      <fieldset className="counter-modal__fieldset">
        <legend>Price &amp; financing</legend>

        <div className="counter-modal__field">
          <label htmlFor={priceId} className="counter-modal__label">Offer price</label>
          <div className="counter-modal__price-wrap">
            <span className="counter-modal__prefix" aria-hidden="true">$</span>
            <input
              id={priceId}
              type="text"
              inputMode="numeric"
              autoComplete="off"
              value={form.offer_price ? Number(form.offer_price).toLocaleString('en-US') : ''}
              onChange={(e) => {
                const raw = e.target.value.replace(/[^0-9]/g, '');
                setPriceTouched(true);
                setPriceConfirmed(false);
                setForm((prev) => ({ ...prev, offer_price: raw }));
              }}
              aria-describedby={
                priceOutOfRange ? `${priceHintId} ${priceWarnId}` : priceHintId
              }
              aria-invalid={needsPriceConfirm || undefined}
              disabled={submitting}
              placeholder="Enter price"
            />
          </div>
          <small id={priceHintId} className="counter-modal__hint">
            Your proposed purchase price.
            {Number.isFinite(Number(listPrice)) && Number(listPrice) > 0 && (
              <> Asking price is ${Number(listPrice).toLocaleString('en-US')}.</>
            )}
          </small>

          {priceOutOfRange && (
            <div
              id={priceWarnId}
              className="counter-modal__price-warning"
              data-testid="counter-price-warning"
            >
              <p className="counter-modal__price-warning-text">
                That is <strong>
                  {Math.abs(deviation * 100).toFixed(0)}% {deviation > 0 ? 'above' : 'below'}
                </strong>{' '}
                the ${Number(listPrice).toLocaleString('en-US')} asking price. Double-check
                you have not added or dropped a digit.
              </p>
              <label className="counter-modal__price-warning-check">
                <input
                  type="checkbox"
                  checked={priceConfirmed}
                  onChange={(e) => setPriceConfirmed(e.target.checked)}
                  disabled={submitting}
                  data-testid="counter-price-confirm"
                />
                <span>Yes, ${priceNumber.toLocaleString('en-US')} is the price I mean.</span>
              </label>
            </div>
          )}
        </div>

        <div className="counter-modal__field">
          <label htmlFor={dpId} className="counter-modal__label">Down payment (%)</label>
          <input
            id={dpId}
            type="number"
            min="0"
            max="100"
            step="0.1"
            value={form.down_payment_percent}
            onChange={(e) =>
              setForm((prev) => ({ ...prev, down_payment_percent: e.target.value === '' ? '' : parseFloat(e.target.value) }))
            }
            aria-describedby={dpHintId}
            disabled={submitting}
          />
          <small id={dpHintId} className="counter-modal__hint">
            Percentage of price paid up-front. 0–100.
          </small>
        </div>

        <div className="counter-modal__field">
          <label htmlFor={earnestId} className="counter-modal__label">Earnest money</label>
          <div className="counter-modal__price-wrap">
            <span className="counter-modal__prefix" aria-hidden="true">$</span>
            <input
              id={earnestId}
              type="text"
              inputMode="numeric"
              autoComplete="off"
              value={form.earnest_money ? Number(form.earnest_money).toLocaleString('en-US') : ''}
              onChange={(e) => {
                const raw = e.target.value.replace(/[^0-9]/g, '');
                setForm((prev) => ({ ...prev, earnest_money: raw }));
              }}
              aria-describedby={earnestHintId}
              aria-invalid={!!earnestError || undefined}
              disabled={submitting}
              placeholder={currentEarnest ? Number(currentEarnest).toLocaleString('en-US') : 'None'}
              data-testid="counter-earnest"
            />
          </div>
          {earnestError ? (
            <small
              id={earnestHintId}
              className="counter-modal__hint counter-modal__hint--error"
              role="alert"
              data-testid="counter-earnest-error"
            >
              {earnestError}
            </small>
          ) : (
            <small id={earnestHintId} className="counter-modal__hint">
              The deposit the buyer puts down. The purchase agreement prints this amount.
              {currentEarnest
                ? <> Leave blank to keep ${Number(currentEarnest).toLocaleString('en-US')}.</>
                : <> Leave blank for no deposit.</>}
            </small>
          )}
        </div>
      </fieldset>

      <fieldset className="counter-modal__fieldset">
        <legend>Closing</legend>
        <div className="counter-modal__field">
          <label htmlFor={closingId} className="counter-modal__label">Closing date</label>
          <input
            id={closingId}
            type="date"
            value={form.closing_date}
            onChange={(e) => setForm((prev) => ({ ...prev, closing_date: e.target.value }))}
            aria-describedby={closingHintId}
            disabled={submitting}
          />
          <small id={closingHintId} className="counter-modal__hint">
            When you propose to close on the property.
          </small>
        </div>
        <div className="counter-modal__field">
          <label htmlFor={expiresId} className="counter-modal__label">Counter valid until</label>
          <input
            id={expiresId}
            type="date"
            value={form.expires_on}
            min={expiresBounds.min}
            max={expiresBounds.max}
            onChange={(e) => setForm((prev) => ({ ...prev, expires_on: e.target.value }))}
            aria-describedby={expiresHintId}
            aria-invalid={!!expirationError || undefined}
            disabled={submitting}
          />
          {expirationError ? (
            <small id={expiresHintId} className="counter-modal__hint counter-modal__hint--error" role="alert">
              {expirationError}
            </small>
          ) : (
            <small id={expiresHintId} className="counter-modal__hint">
              The {recipient} must accept your counter in cutthe6.com by{' '}
              <strong>{deadlineTimeLabel(form.expires_on, timeZone)}</strong> on this date,
              the property&apos;s local time, or it expires.
              {currentRole === 'buyer' && " You can withdraw it any time before it's accepted."}
              {' '}Once you counter, the earlier offer can no longer be accepted.
            </small>
          )}
          <OfferDeadlineDisclaimer />
        </div>
      </fieldset>

      <fieldset className="counter-modal__fieldset">
        <legend>Contingencies to keep</legend>
        {hasLandContingencies && <LandContingencyNote viewerRole={currentRole} />}
        {showDeadlineNote && <ContingencyDeadlineNote viewerRole={currentRole} />}
        {contingencyErrorActive && (
          <ContingencyErrorBanner
            error={contingencyError}
            options={options}
            extraKeys={[
              ...(leadPaintAskedMode ? ['lead_paint'] : []),
              ...(cureAsked ? [CURE_ELECTION_FIELD] : []),
              ...(financingAsked ? [FINANCING_TERMS_FIELD] : []),
              ...(termsCategory ? [TITLE_PERIODS_FIELD] : []),
            ]}
          />
        )}
        {!form.contingenciesTouched && deadlineError && !deadlinesInvalid && (
          <p className="counter-modal__hint" data-testid="counter-contingencies-unchanged">
            {CONTINGENCIES_UNCHANGED_NOTE}
          </p>
        )}
        <div className="counter-modal__checkboxes">
          {options.map(({ key, label, deadline, deadlineRequired, unavailable }) => (
            <div key={key} className="counter-modal__check-wrap">
              <label className="counter-modal__check">
                <input
                  type="checkbox"
                  checked={!!form.contingencies[key]}
                  onChange={(e) => handleContingencyChange(key, e.target.checked)}
                  disabled={submitting}
                />
                <span>{label}</span>
              </label>
              {unavailable && form.contingencies[key] && (
                <ContingencyFieldError field={`${key}-unavailable`} message={CONTINGENCY_UNAVAILABLE_MESSAGE} />
              )}
              <ContingencyFieldError field={key} message={serverErrors[key]} />
              {deadline && form.contingencies[key] && (
                <ContingencyDeadlineField
                  contingencyKey={key}
                  label={label}
                  value={form.contingencies[deadlineKey(key)]}
                  required={deadlineRequired}
                  serverError={serverErrors[deadlineKey(key)]}
                  onChange={(text) => handleDeadlineChange(key, text)}
                  disabled={submitting}
                />
              )}
            </div>
          ))}
        </div>
        <FinancingTermsField
          category={termsCategory}
          contingencies={form.contingencies}
          onChange={handleTermsChange}
          amount={financingLoanAmount(form.offer_price, form.down_payment_percent)}
          downPaymentPercent={form.down_payment_percent}
          viewerRole={currentRole}
          disabled={submitting}
          serverErrors={serverErrors}
        />
        <TitlePeriodsField
          category={termsCategory}
          contingencies={form.contingencies}
          onChange={handleTermsChange}
          closingDate={form.closing_date}
          timeZone={timeZone}
          viewerRole={currentRole}
          disabled={submitting}
          serverErrors={serverErrors}
        />
        <LeadPaintElectionField
          mode={leadPaintAskedMode}
          contingencies={form.contingencies}
          onChange={(contingencies) => setForm((prev) => ({ ...prev, contingencies, contingenciesTouched: true }))}
          disabled={submitting}
          serverErrors={serverErrors}
        />
        {currentRole === 'seller' && (
          <LeadPaintElectionCarried mode={leadPaintMode} contingencies={form.contingencies} />
        )}
        <CureElectionField
          category={cureCategory}
          contingencies={form.contingencies}
          onChange={(contingencies) => setForm((prev) => ({ ...prev, contingencies, contingenciesTouched: true }))}
          disabled={submitting}
          serverErrors={serverErrors}
        />
      </fieldset>

      <fieldset className="counter-modal__fieldset">
        <legend className="counter-sr-only">Description</legend>
        <div className="counter-modal__field">
          <label htmlFor={descId} className="counter-modal__label">
            Describe what's changing for the other party
          </label>
          <textarea
            id={descId}
            rows={3}
            value={form.changes_description}
            onChange={(e) =>
              setForm((prev) => ({ ...prev, changes_description: e.target.value }))
            }
            placeholder="Optional — explain what changed and why."
            disabled={submitting}
            maxLength={1000}
          />
        </div>
      </fieldset>
    </Modal>
  );
}
